import { corptaxCustomLightTheme } from '@cscglobal-tbs/react-components-core';
import { AriaLiveAnnouncer, FluentProvider } from '@fluentui/react-components';
import { PostHogProvider } from '@posthog/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { flushSync } from 'react-dom';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router';
import './assets/css/sync-fusion/customSyncFusionStyles.css';
import { QUERY_CLIENT_MAX_RETRIES, VITE_QUERY_CLIENT_STALE_TIME_MS } from './constants';
import { CurrentEnterpriseContextProvider } from './contexts/currentEnterpriseContext';
import { FunctionalAccessContextProvider } from './contexts/functionalAccessContext';
import NotificationProvider from './contexts/notificationContext';
import ServiceDiscoveryContextProvider from './contexts/serviceDiscoveryContext';
import { TaxReturnCalculationProvider } from './contexts/taxReturnCalculationContext';
import UserSettingsProvider from './contexts/userSettingsContext';
import { initI18n } from './i18n';
import './index.css';
import { registerDefaultSyncfusionLicense } from './registerSyncfusionLicense';
import routesConfig from './routes';
import { posthog } from './telemetry/posthog';
// Side-effect: apply app theme token overrides before render.
import './theme';
import { fluentCustomStyleHooks } from './theme';

registerDefaultSyncfusionLicense();

// Requests to the server will be cached for the time specified by VITE_QUERY_CLIENT_STALE_TIME_MS
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            // assign a default retry policy:
            // retry up to the number of times specified by QUERY_CLIENT_MAX_RETRIES
            // if the error is a 400 level error, don't retry, since it is likely a client error
            // if QUERY_CLIENT_MAX_RETRIES is not set, don't retry
            retry: (failureCount: number, error: unknown) => {
                const axiosError = error as AxiosError;
                if (axiosError?.response?.status && axiosError.response.status >= 400 && axiosError.response.status < 500) {
                    return false;
                }

                return QUERY_CLIENT_MAX_RETRIES ? failureCount < parseInt(QUERY_CLIENT_MAX_RETRIES) : false;
            },
            staleTime: VITE_QUERY_CLIENT_STALE_TIME_MS,
        },
    },
});

const router = createBrowserRouter(routesConfig);

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

(async () => {
    await initI18n();

    root.render(
        <FluentProvider theme={corptaxCustomLightTheme} customStyleHooks_unstable={fluentCustomStyleHooks}>
            <AriaLiveAnnouncer>
                <CurrentEnterpriseContextProvider>
                    <QueryClientProvider client={queryClient}>
                        <PostHogProvider client={posthog}>
                            <ServiceDiscoveryContextProvider>
                                <FunctionalAccessContextProvider>
                                    <UserSettingsProvider>
                                        <TaxReturnCalculationProvider>
                                            <NotificationProvider>
                                                <RouterProvider router={router} flushSync={flushSync} />
                                            </NotificationProvider>
                                        </TaxReturnCalculationProvider>
                                    </UserSettingsProvider>
                                </FunctionalAccessContextProvider>
                            </ServiceDiscoveryContextProvider>
                        </PostHogProvider>
                    </QueryClientProvider>
                </CurrentEnterpriseContextProvider>
            </AriaLiveAnnouncer>
        </FluentProvider>
    );
})();
